import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Layout, { TopBar } from '../components/Layout';
import { Meta, Display, Chip } from '../components/Atoms';
import { LoadingState, ErrorState, EmptyState } from '../components/states';
import { useApi, useMultipleApi } from '../lib/api';

/**
 * PlantProblems - lista chorób, szkodników i niedoborów roślin.
 *
 * Endpointy:
 *   GET /api/plant-problems?search=...&category=...&severity=...
 *   GET /api/plant-problems/categories/list
 *
 * Filtry:
 *   - search (po name/common_name/symptoms)
 *   - category (szkodnik/choroba_grzybowa/choroba_bakteryjna/niedobor)
 *   - severity (krytyczna/wysoka/średnia/niska)
 */

const CATEGORY_LABEL = {
  szkodnik: 'Szkodnik',
  choroba_grzybowa: 'Choroba grzybowa',
  choroba_bakteryjna: 'Choroba bakteryjna',
  niedobor: 'Niedobór',
};

const CATEGORY_ICON = {
  szkodnik: '🐛',
  choroba_grzybowa: '🍄',
  choroba_bakteryjna: '🦠',
  niedobor: '⚗',
};

const SEVERITY_TONE = {
  krytyczna: 'rust',
  wysoka: 'rust',
  średnia: 'amber',
  niska: 'sage',
};

const SEVERITY_LABEL = {
  krytyczna: 'krytyczna',
  wysoka: 'wysoka',
  średnia: 'średnia',
  niska: 'niska',
};

function ProblemCard({ problem }) {
  const tone = SEVERITY_TONE[problem.severity] || 'dim';
  const icon = CATEGORY_ICON[problem.category] || '⚠';
  const affects = (problem.affects_plants || '').split(',').map(p => p.trim()).filter(Boolean);

  return (
    <Link to={`/problemy/${problem.id}`} style={{ textDecoration: 'none' }}>
      <div style={{
        padding: '20px 22px', borderRadius: 14,
        background: 'var(--t-bg-elev)',
        border: '1px solid var(--t-line)',
        marginBottom: 12,
        transition: 'border-color 200ms ease, transform 200ms ease',
        cursor: 'pointer',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'var(--t-sage)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--t-line)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{
            fontSize: 28, lineHeight: 1, flexShrink: 0, marginTop: 2,
          }}>{icon}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              marginBottom: 6,
            }}>
              <Display size={20} style={{
                display: 'inline', letterSpacing: '-.02em',
              }}>{problem.name}</Display>
              <Chip tone={tone}>{SEVERITY_LABEL[problem.severity] || problem.severity}</Chip>
            </div>
            {problem.common_name && (
              <div style={{
                fontSize: 12, color: 'var(--t-text-mute)',
                fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
                marginBottom: 8,
              }}>też: {problem.common_name}</div>
            )}
            {problem.description && (
              <div style={{
                fontSize: 13, color: 'var(--t-text-dim)',
                lineHeight: 1.5, marginBottom: 10,
              }}>{problem.description}</div>
            )}
            {affects.length > 0 && (
              <div style={{
                display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center',
              }}>
                <Meta>Dotyczy:</Meta>
                {affects.slice(0, 5).map(p => (
                  <span key={p} style={{
                    fontSize: 11, color: 'var(--t-text)',
                    fontFamily: 'var(--t-mono)', letterSpacing: '.04em',
                    padding: '2px 8px', borderRadius: 6,
                    background: 'var(--t-bg)',
                    border: '1px solid var(--t-line)',
                  }}>{p}</span>
                ))}
                {affects.length > 5 && (
                  <span style={{
                    fontSize: 11, color: 'var(--t-text-mute)',
                    fontFamily: 'var(--t-mono)',
                  }}>+{affects.length - 5}</span>
                )}
              </div>
            )}
            {problem.peak_season && (
              <div style={{
                marginTop: 10, fontSize: 11,
                color: 'var(--t-amber)',
                fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
              }}>↻ Sezon: {problem.peak_season.replace(/,/g, ' · ').replace(/_/g, ' ')}</div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function PlantProblems() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('');

  // Buduj URL z filtrami
  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (search.trim().length >= 2) p.set('search', search.trim());
    if (category) p.set('category', category);
    if (severity) p.set('severity', severity);
    const qs = p.toString();
    return qs ? `?${qs}` : '';
  }, [search, category, severity]);

  const { results, loading, errors } = useMultipleApi({
    problems: `/api/plant-problems${params}`,
    cats: '/api/plant-problems/categories/list',
  });

  if (errors.problems?.includes('autoryz') || errors.problems === 'Brak tokenu autoryzacji') {
    return <Layout><ErrorState error={{ status: 401, message: errors.problems }} /></Layout>;
  }
  if (loading) return <Layout><LoadingState label="Ładuję problemy roślin…" /></Layout>;

  const problems = results.problems || [];
  const cats = results.cats || [];

  return (
    <Layout topBar={<TopBar
      left={<Meta>Choroby i szkodniki</Meta>}
    />}>
      <div style={{
        maxWidth: 880, margin: '0 auto',
        padding: '32px 32px 60px',
      }}>
        <Meta color="sage" style={{ marginBottom: 10 }}>Diagnoza</Meta>
        <Display size={'clamp(40px, 7vw, 56px)'} style={{
          display: 'block', letterSpacing: '-.03em', lineHeight: 1,
          marginBottom: 14,
        }}>Coś się dzieje z roślinami?</Display>
        <div style={{
          fontSize: 15, color: 'var(--t-text-dim)',
          lineHeight: 1.55, marginBottom: 32, maxWidth: 580,
        }}>
          Baza chorób, szkodników i niedoborów występujących w polskich ogrodach.
          Wyszukaj po objawach albo wybierz kategorię.
        </div>

        {/* Search */}
        <div style={{ marginBottom: 18 }}>
          <input
            type="search"
            aria-label="Szukaj po objawach"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Szukaj po objawach (np. żółte plamy, mszyce, dziury w liściach)…"
            style={{
              width: '100%',
              padding: '14px 18px', borderRadius: 12,
              background: 'var(--t-bg-elev)',
              border: `1px solid ${search ? 'var(--t-sage)' : 'var(--t-line)'}`,
              color: 'var(--t-text)',
              fontFamily: 'var(--t-sans)', fontSize: 15,
              letterSpacing: '-.01em', outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Filtry kategorii */}
        <div style={{
          display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12,
        }}>
          <button
            type="button"
            onClick={() => setCategory('')}
            style={{
              padding: '8px 14px', borderRadius: 999,
              background: !category ? 'var(--t-sage)' : 'transparent',
              border: `1px solid ${!category ? 'var(--t-sage)' : 'var(--t-line)'}`,
              color: !category ? 'var(--t-bg)' : 'var(--t-text-dim)',
              cursor: 'pointer', fontSize: 12,
              fontFamily: 'var(--t-mono)', letterSpacing: '.08em',
              textTransform: 'uppercase',
            }}
          >Wszystkie</button>
          {cats.map(c => {
            const isOn = category === c.category;
            return (
              <button
                key={c.category}
                type="button"
                onClick={() => setCategory(isOn ? '' : c.category)}
                style={{
                  padding: '8px 14px', borderRadius: 999,
                  background: isOn ? 'var(--t-sage)' : 'transparent',
                  border: `1px solid ${isOn ? 'var(--t-sage)' : 'var(--t-line)'}`,
                  color: isOn ? 'var(--t-bg)' : 'var(--t-text-dim)',
                  cursor: 'pointer', fontSize: 12,
                  fontFamily: 'var(--t-mono)', letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                <span>{CATEGORY_ICON[c.category] || '⚠'}</span>
                {CATEGORY_LABEL[c.category] || c.category}
                <span style={{ opacity: 0.7, fontSize: 10 }}>{c.count}</span>
              </button>
            );
          })}
        </div>

        {/* Filtr severity */}
        <div style={{
          display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 24,
        }}>
          <Meta style={{ marginRight: 4, alignSelf: 'center' }}>Powaga:</Meta>
          {['krytyczna', 'wysoka', 'średnia', 'niska'].map(s => {
            const isOn = severity === s;
            const tone = SEVERITY_TONE[s];
            const color = tone === 'rust' ? 'var(--t-rust)' :
                          tone === 'amber' ? 'var(--t-amber)' :
                          'var(--t-sage)';
            return (
              <button
                key={s}
                type="button"
                onClick={() => setSeverity(isOn ? '' : s)}
                style={{
                  padding: '4px 10px', borderRadius: 999,
                  background: isOn ? color : 'transparent',
                  border: `1px solid ${isOn ? color : 'var(--t-line-2)'}`,
                  color: isOn ? 'var(--t-bg)' : color,
                  cursor: 'pointer', fontSize: 10,
                  fontFamily: 'var(--t-mono)', letterSpacing: '.12em',
                  textTransform: 'uppercase',
                }}
              >{s}</button>
            );
          })}
        </div>

        {/* Wyniki */}
        <div style={{
          marginBottom: 14, display: 'flex', justifyContent: 'space-between',
          alignItems: 'baseline',
        }}>
          <Meta>{problems.length} {problems.length === 1 ? 'problem' : problems.length < 5 ? 'problemy' : 'problemów'}</Meta>
          {(search || category || severity) && (
            <button
              type="button"
              onClick={() => { setSearch(''); setCategory(''); setSeverity(''); }}
              style={{
                background: 'transparent', border: 'none',
                color: 'var(--t-sage)', cursor: 'pointer',
                fontFamily: 'var(--t-mono)', fontSize: 11,
                letterSpacing: '.16em', textTransform: 'uppercase',
              }}
            >Wyczyść filtry ✕</button>
          )}
        </div>

        {problems.length === 0 ? (
          <EmptyState
            title={search || category || severity ? 'Brak wyników' : 'Brak problemów w bazie'}
            description={search
              ? 'Spróbuj innej frazy albo wyczyść filtry.'
              : 'Skontaktuj się z administratorem.'
            }
          />
        ) : (
          <div>
            {problems.map(p => <ProblemCard key={p.id} problem={p} />)}
          </div>
        )}

        {/* Footer info */}
        <div style={{
          marginTop: 40, paddingTop: 20,
          borderTop: '1px solid var(--t-line)',
          textAlign: 'center',
          fontFamily: 'var(--t-mono)', fontSize: 11,
          letterSpacing: '.12em', textTransform: 'uppercase',
          color: 'var(--t-text-mute)',
        }}>
          Baza polska · Zaufana literatura ogrodnicza
        </div>
      </div>
    </Layout>
  );
}
